import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogContent,
  MenuList,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ContextualAiDialog from '../ai/ContextualAiDialog';
import { useContextualAiLayers } from '../ai/ContextualAiLayerContext';
import { ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from '../responsive';
import useViewportMode from '../../hooks/useViewportMode';
import AiReviewPanel from '../../ui-components/AiReviewPanel';
import {
  AiReviewReplayAdapter,
  AiReviewReplayFixture,
  AI_REVIEW_REPLAY_TALENT_ID,
  AI_REVIEW_REPLAY_VERSION,
} from '../../services/AiReviewReplayService';

const SupervisorRatingPreview = ({ initialValue }: { initialValue: string }) => {
  const layers = useContextualAiLayers();
  const [value, setValue] = useState(initialValue);
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open || !layers) return;
    layers.openTransient();
    return layers.closeTransient;
  }, [layers, open]);

  const closeAndRestoreFocus = () => {
    setOpen(false);
    requestAnimationFrame(() => buttonRef.current?.focus());
  };
  const buttonRect = open ? buttonRef.current?.getBoundingClientRect() : null;

  return <>
    <Box><Button
      ref={buttonRef}
      fullWidth
      size="small"
      variant="outlined"
      aria-label="Supervisor rating preview"
      aria-haspopup="listbox"
      aria-expanded={open}
      onClick={() => setOpen(current => !current)}
      sx={{ justifyContent: 'space-between', textTransform: 'none' }}>
      {value}
    </Button></Box>
    {open && buttonRect && createPortal(<Paper
      elevation={12}
      sx={{
        position: 'fixed',
        zIndex: layers?.transientZIndex ?? 1400,
        top: buttonRect.bottom + 4,
        left: buttonRect.left,
        width: buttonRect.width,
      }}>
        <MenuList
          role="listbox"
          aria-label="Supervisor rating options"
          autoFocusItem
          onKeyDown={event => {
            if (event.key === 'Tab') {
              const options = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('[role="option"]:not([aria-disabled="true"])'));
              const currentIndex = options.indexOf(document.activeElement as HTMLElement);
              const nextIndex = event.shiftKey
                ? (currentIndex <= 0 ? options.length - 1 : currentIndex - 1)
                : (currentIndex < 0 || currentIndex === options.length - 1 ? 0 : currentIndex + 1);
              event.preventDefault();
              event.stopPropagation();
              options[nextIndex]?.focus();
              return;
            }
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              closeAndRestoreFocus();
            }
          }}>
          {['Never', 'Rarely', 'Sometimes', 'Usually', 'Always'].map(option => <MenuItem
            key={option}
            role="option"
            aria-selected={value === option}
            selected={value === option}
            onClick={() => {
              setValue(option);
              closeAndRestoreFocus();
            }}>
            {option}
          </MenuItem>)}
        </MenuList>
    </Paper>, document.body)}
  </>;
};

const AiReviewReplayPresentation = ({ fixture }: { fixture: AiReviewReplayFixture }) => {
  const viewport = useViewportMode();
  const adapter = useMemo(() => new AiReviewReplayAdapter(), []);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [hostDraft, setHostDraft] = useState(adapter.getHostDraft());

  const refreshHost = async () => setHostDraft(adapter.getHostDraft());

  return <ResponsiveDialog
    open
    fullWidth
    widthTier="compact"
    sizingStrategy="content"
    desktopViewportGutter={16}
    aria-labelledby="ai-review-replay-title"
    PaperProps={{ sx: { overflow: 'hidden' } }}>
    <ResponsiveDialogHeader
      id="ai-review-replay-title"
      title="Jordan Avery · Ongoing assessment"
      subtitle="Field Service Technician"
      actions={<Chip label="Synthetic demonstration · Read only" color="info" variant="outlined" />}
      viewportOverride={viewport.mode} />
    <DialogContent dividers sx={{ minHeight: 0, overflowY: 'auto', p: { xs: 2, md: 2.5 } }}>
      <Box
        ref={hostRef}
        data-testid="ai-review-replay-host"
        data-layout={viewport.isDesktop ? 'desktop-host' : viewport.isTablet ? 'tablet-host' : 'phone-host'}
        data-fixture-fingerprint={AI_REVIEW_REPLAY_VERSION}
        sx={{ width: { xs: '100%', md: 680 }, maxWidth: '100%' }}>
        <Stack spacing={2}>
          <Alert severity="info">
            Synthetic training records. Suggestions remain under supervisor control and no organization record can change.
          </Alert>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} justifyContent="space-between" alignItems={{ sm: 'center' }}>
              <Box>
                <Typography variant="overline" color="text.secondary">Current draft · Communication</Typography>
                <Typography fontWeight={800}>Escalates ambiguous safety risks before continuing work</Typography>
              </Box>
              <Chip data-testid="host-escalation-rating" label={hostDraft.EscalationRating} color={hostDraft.EscalationRating === fixture.BaselineHostDraft.EscalationRating ? 'default' : 'success'} />
            </Stack>
            <Typography data-testid="host-escalation-note" variant="body2" color="text.secondary" sx={{ mt: 1 }}>{hostDraft.EscalationNote}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline" color="text.secondary">Development goal staged for supervisor review</Typography>
            <Typography data-testid="host-development-goal" variant="body2">{hostDraft.DevelopmentGoal || 'No new goal is applied.'}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" gap={1} alignItems="center"><AutoAwesomeRoundedIcon color="primary" /><Typography fontWeight={800}>Supervisor observation</Typography></Stack>
            <Typography variant="body2" sx={{ mt: 1 }}>{fixture.Prompt}</Typography>
          </Paper>
        </Stack>
      </Box>
    </DialogContent>
    <StickyDialogActions viewportOverride={viewport.mode}>
      <Button variant="contained" startIcon={<AutoAwesomeRoundedIcon />} onClick={() => setAiOpen(true)}>Open Synergist AI</Button>
    </StickyDialogActions>
    <ContextualAiDialog
      open={aiOpen}
      onClose={() => setAiOpen(false)}
      anchorEl={hostRef.current}
      title="Synergist AI — Review"
      workflow="ongoing-assessment">
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
        <Box sx={{ px: 1.5, pt: 1.5 }}>
          <SupervisorRatingPreview initialValue={fixture.BaselineHostDraft.EscalationRating} />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <AiReviewPanel
            talentId={AI_REVIEW_REPLAY_TALENT_ID}
            onDraftChanged={refreshHost}
            fillAvailableHeight
            viewportMode={viewport.mode}
            dataSource={adapter}
            voiceDisabled />
        </Box>
      </Box>
    </ContextualAiDialog>
  </ResponsiveDialog>;
};

export default AiReviewReplayPresentation;
